<template>
  <div
    v-if="items.length"
    class="lpu-photos"
    :class="{ 'mx-n2': isMobile }"
    data-qa="lpu_photos"
  >
    <LpuPhotosDialog
      ref="lightboxRef"
      :items="items"
    />

    <CoreSwiper
      v-if="isMobile"
      class="px-2"
      slides-per-view="auto"
      :space-between="8"
      :has-pagination="false"
      :has-navigation="false"
      :placeholder-min-height="192"
      free-mode
    >
      <template #slides="{ slideAttrs }">
        <div
          v-for="(item, index) in items"
          :key="`${item.id ?? item.src}-${index}`"
          v-bind="slideAttrs"
        >
          <button
            type="button"
            class="lpu-photos__slide"
            data-qa="lpu_photos_tile"
            @click="open(index)"
          >
            <img
              class="lpu-photos__image"
              :src="item.src"
              :alt="item.alt || ''"
              :title="item.title || undefined"
              loading="lazy"
            >
          </button>
        </div>
      </template>
    </CoreSwiper>

    <div
      v-else
      class="lpu-photos__mosaic"
      :class="{
        'lpu-photos__mosaic--cols-1': mosaicItems.length < 2,
        'lpu-photos__mosaic--cols-2': mosaicItems.length === 2,
        'lpu-photos__mosaic--cols-3': mosaicItems.length === 3,
        'lpu-photos__mosaic--cols-4': mosaicItems.length === 4,
        'lpu-photos__mosaic--cols-5': mosaicItems.length > 4,
      }"
    >
      <button
        v-for="(item, index) in mosaicItems"
        :key="`${item.id ?? item.src}-${index}`"
        type="button"
        class="lpu-photos__tile"
        :class="{
          'lpu-photos__tile--tall': isTallTile(index),
          'lpu-photos__tile--wide': isWideTile(index),
        }"
        data-qa="lpu_photos_tile"
        @click="open(index)"
      >
        <img
          class="lpu-photos__image"
          :src="item.src"
          :alt="item.alt || ''"
          :title="item.title || undefined"
          loading="lazy"
        >
        <span
          v-if="index === mosaicItems.length - 1 && hasMoreOverlay"
          class="lpu-photos__more text-center"
          data-qa="lpu_photos_more"
        >
          <span class="lpu-photos__more-count">
            {{ moreCount }}
          </span>
          <span class="text-body-1">
            {{ tStatic('фото') }}
          </span>
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import LpuPhotosDialog from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhotos/components/LpuPhotosDialog.vue'
import type { LpuPhoto } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhotos/types'

const MOSAIC_LIMIT = 5

type PhotoItem = CoreImageLightboxItem

type LightboxExpose = {
  open: (_index: number) => void
}

const props = withDefaults(defineProps<{
  photos: LpuPhoto[]
  lpuName?: string
}>(), {
  lpuName: undefined,
})

const isMobile = useIsMobile()
const lightboxRef = ref<LightboxExpose | null>(null)

function getPhotoAlt(photo: LpuPhoto) {
  const title = photo.title || ''

  if (props.lpuName && title) {
    return `${props.lpuName} - ${title}`
  }

  return title || props.lpuName || ''
}

const items = computed((): PhotoItem[] => (
  props.photos
    .filter(photo => Boolean(photo.img_l?.url || photo.img_s?.url))
    .map(photo => ({
      id: photo.id,
      src: (photo.img_l?.url || photo.img_s?.url) as string,
      title: photo.title || undefined,
      alt: getPhotoAlt(photo),
    }))
))

const mosaicItems = computed(() => items.value.slice(0, MOSAIC_LIMIT))
const hasMoreOverlay = computed(() => items.value.length > MOSAIC_LIMIT)
const moreCount = computed(() => items.value.length - mosaicItems.value.length + 1)

/**
 * На всю высоту мозаики: одно и два фото, первая плитка при 4–5.
 * Три фото — один ряд, без span на две строки.
 */
function isTallTile(index: number) {
  const count = mosaicItems.value.length

  if (count === 3) {
    return false
  }

  return count <= 2 || index === 0
}

/** Вторая плитка на всю ширину правой половины при четырёх фото. */
function isWideTile(index: number) {
  return mosaicItems.value.length === 4 && index === 1
}

function open(index: number) {
  lightboxRef.value?.open(index)
}
</script>

<style scoped lang="scss">
@import 'www/themes/doctors/common/variables';

.lpu-photos {
  &__mosaic {
    display: grid;
    grid-template-rows: 1fr 1fr;
    grid-auto-flow: column;
    gap: 12px;
    height: 370px;
  }

  &__mosaic--cols-1 {
    grid-template-columns: 1fr;
  }

  &__mosaic--cols-2 {
    grid-template-columns: 1fr 1fr;
  }

  &__mosaic--cols-3 {
    grid-template-columns: 568fr 278fr 278fr;
    grid-template-rows: 1fr;
    grid-auto-flow: row;
  }

  &__mosaic--cols-4 {
    grid-template-columns: 568fr 278fr 278fr;
    grid-auto-flow: row;
  }

  &__mosaic--cols-5 {
    grid-template-columns: 568fr 318fr 238fr;
  }

  &__tile,
  &__slide {
    position: relative;
    display: block;
    min-width: 0;
    padding: 0;
    border: 0;
    background-color: var(--lpu-branding-color-background, $ui-kit-bg-primary);
    color: var(--lpu-branding-color-text, $ui-kit-primary);
    line-height: 0;
    cursor: pointer;
    overflow: hidden;
    border-radius: 20px;
  }

  &__tile--tall {
    grid-row: 1 / 3;
  }

  &__tile--wide {
    grid-column: 2 / 4;
  }

  &__slide {
    width: 290px;
    height: 192px;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 20px;
    background-color: var(--lpu-branding-color-background, $ui-kit-bg-primary);
    color: var(--lpu-branding-color-text, $ui-kit-primary);
  }

  &__more {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: 20px;
    background-color: rgba(0, 0, 0, 0.5);
    color: $ui-kit-bg-gray-0;
  }

  &__more-count {
    font-size: 24px;
    font-weight: 600;
    line-height: 20px;
  }
}
</style>
